import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * media gallery with reliable infinite loop on both desktop & mobile
 */
export class MediaGallery extends Component {
  #controller = new AbortController();

  // state
  #scroller = null;          // native horizontal scroller (mobile layouts)
  #scrollGuard = false;      // debounce guard for scroll-end
  #userScrollArmed = false;  // only loop on user-driven scroll end
  #loopTimer = null;         // timer for auto-jump after last slide
  #raf = null;               // rAF for index tracking
  #lastSeenIndex = -1;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // 初期化はハイドレーション完了を待ってから
    this.#initWithRetry(25, 80);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();

    if (this.#loopTimer) clearTimeout(this.#loopTimer);
    if (this.#raf) cancelAnimationFrame(this.#raf);
  }

  // ---------- init with retry (wait hydration) ----------
  async #initWithRetry(tries = 25, delay = 80) {
    for (let i = 0; i < tries; i++) {
      if (this.slideshow && (this.slideshow.slides?.length || 0) > 1) break;
      await new Promise(r => setTimeout(r, delay));
    }

    // ① どの経路でもインデックスが循環するよう select をラップ
    this.#enableInfiniteLoopBySelect();

    // ② 「最後を見終えたら自動で先頭へ」：インデックス監視で確実に発火（PC/スマホ共通）
    this.#autoJumpToFirstAfterLast();

    // ③ モバイルのネイティブ横スク（scroll-snap）にも対応（端で復帰）
    this.#wireMobileScrollLoop();
  }

  // ---------- ① select() ラップで常に循環 ----------
  #enableInfiniteLoopBySelect() {
    const ss = this.slideshow;
    if (!ss) return;

    const slides = ss.slides || [];
    if (!Array.isArray(slides) || slides.length <= 1) return;
    if (ss.__selectWrapped) return;

    const originalSelect = typeof ss.select === 'function' ? ss.select.bind(ss) : null;
    if (!originalSelect) return;

    ss.select = (index, ...rest) => {
      const total = (ss.slides || []).length;
      if (!total) return originalSelect(index, ...rest);
      if (typeof index !== 'number' || Number.isNaN(index)) return originalSelect(index, ...rest);

      // 0..total-1 に正規化（負方向もOK）
      let normalized = index % total;
      if (normalized < 0) normalized += total;

      return originalSelect(normalized, ...rest);
    };

    ss.__selectWrapped = true;
  }

  // ---------- ② “最後を見終えたら”自動で先頭へ ----------
  #autoJumpToFirstAfterLast() {
    const ss = this.slideshow;
    const total = ss?.slides?.length || 0;
    if (!ss || total <= 1) return;

    const tick = () => {
      const idx = this.#getCurrentIndex(ss);
      if (idx !== this.#lastSeenIndex) {
        this.#lastSeenIndex = idx;

        // 最後のスライドを“表示した”瞬間にスケジュール（見終わった体感に少し間を持たせる）
        if (idx === total - 1) {
          if (this.#loopTimer) clearTimeout(this.#loopTimer);
          this.#loopTimer = setTimeout(() => {
            // ここで自動的に先頭へ戻す（アニメ有り）
            ss.select(0, undefined, { animate: true });
          }, 500); // 体感調整：0～700msで好み
        } else {
          if (this.#loopTimer) clearTimeout(this.#loopTimer);
        }
      }
      this.#raf = requestAnimationFrame(tick);
    };

    this.#raf = requestAnimationFrame(tick);
  }

  // slideshow から現在インデックスを取得（api が無ければ DOM から推定）
  #getCurrentIndex(ss) {
    if (!ss) return 0;
    if (typeof ss.index === 'number') return ss.index;

    // DOM 推定：aria-current / data-selected などが付くケースに対応
    const nodes = ss.slides || [];
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      if (!el) continue;
      const ac = el.getAttribute?.('aria-current');
      const ds = el.getAttribute?.('data-selected');
      if (ac === 'true' || ds === 'true' || el.classList?.contains('is-active') || el.classList?.contains('is-selected')) {
        return i;
      }
    }
    // フォールバック
    return 0;
  }

  // ---------- ③ モバイル横スクの端復帰（ネイティブ scroll-snap 用） ----------
  #wireMobileScrollLoop() {
    this.#scroller =
      this.querySelector('[data-media-gallery]') ||
      this.slideshow?.container ||
      this.querySelector('.media-scroller, [data-slideshow], .slideshow, .slider') ||
      null;

    const el = this.#scroller;
    if (!el) return;

    // ユーザー操作直後のみループ判定（誤発火防止）
    const arm = () => {
      this.#userScrollArmed = true;
      setTimeout(() => (this.#userScrollArmed = false), 700);
    };
    el.addEventListener('pointerdown', arm, { signal: this.#controller.signal, passive: true });
    el.addEventListener('touchstart', arm, { signal: this.#controller.signal, passive: true });
    el.addEventListener('keydown', arm, { signal: this.#controller.signal });

    let timer;
    el.addEventListener(
      'scroll',
      () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.#maybeLoopOnScrollEnd(), 100); // 慣性スクロール終了を待つ
      },
      { signal: this.#controller.signal, passive: true },
    );
  }

  #maybeLoopOnScrollEnd() {
    if (this.#scrollGuard || !this.#userScrollArmed || !this.#scroller) return;

    const el = this.#scroller;
    const atEnd = Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth - 1;
    const atStart = Math.floor(el.scrollLeft) <= 0;

    if (!atEnd && !atStart) return;

    this.#scrollGuard = true;
    setTimeout(() => (this.#scrollGuard = false), 250);

    const ss = this.slideshow;
    const total = ss?.slides?.length || 0;

    if (atEnd) {
      if (ss && total > 1) {
        ss.select(0, undefined, { animate: true });
      } else {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      }
    } else if (atStart) {
      if (ss && total > 1) {
        ss.select(total - 1, undefined, { animate: true });
      } else {
        el.scrollTo({ left: el.scrollWidth - el.clientWidth, behavior: 'smooth' });
      }
    }
  }

  // ---------- existing handlers ----------
  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
    // 新しい要素の connectedCallback が走り、上記初期化が再度適用されます
  };

  #handleZoomMediaSelected = async (event) => {
    // ズーム切替は従来どおりアニメ無し
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  // ---------- public api ----------
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
